import { ExternalConnectionsProviders } from '@griply/types';
import { CircularProgress } from '@mui/material';
import { useExternalCalendarConnections } from 'features/external-connections';
import React, { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Google, Outlook, Plus } from 'shared/assets/icons';
import { ExternalCalendarConnection } from 'shared/components/connected/external-calendar-connection';
import { Icon } from 'shared/components/ui/icon';
import {
  PopupMenu,
  PopupMenuButton,
  PopupMenuList,
  PopupMenuListItem,
} from 'shared/components/ui/popup-menu';
import { useRedirectToExternalAuthMutation } from 'shared/hooks/queries/use-redirect-to-external-auth-mutation';
import { useOpenMenu } from 'shared/hooks/use-open-menu';

import { CalendarSyncOptionsList } from './calendar-sync-options-list';
import * as Styled from './external-calendar-list.style';

export const ExternalCalendarList: React.FC = () => {
  const buttonRef = useRef<HTMLButtonElement>(null);
  const { t } = useTranslation();
  const { menuOpen, openMenu, closeMenu } = useOpenMenu();

  const externalCalendars = useExternalCalendarConnections();
  const { mutate: redirectToExternalAuth, isPending } =
    useRedirectToExternalAuthMutation();

  const onAddGoogle = () => {
    !isPending && redirectToExternalAuth(ExternalConnectionsProviders.Google);
    closeMenu();
  };
  const onAddOutlook = () => {
    !isPending && redirectToExternalAuth(ExternalConnectionsProviders.Outlook);
    closeMenu();
  };

  return (
    <Styled.List>
      {externalCalendars.map((calendarConnection) => (
        <Styled.ListItem key={calendarConnection.id}>
          <ExternalCalendarConnection connection={calendarConnection} />
        </Styled.ListItem>
      ))}

      <Styled.ListItem>
        <Styled.AddButton
          ref={buttonRef}
          onClick={openMenu}
          disabled={isPending}
        >
          <Styled.AddIconContainer>
            {isPending ? (
              <CircularProgress size="1em" color="inherit" />
            ) : (
              <Icon icon={Plus} />
            )}
          </Styled.AddIconContainer>
          {t('forms.user-settings.sections.calendar.add-external.label')}
        </Styled.AddButton>

        {menuOpen && (
          <PopupMenu referenceElement={buttonRef} onOutside={closeMenu}>
            <PopupMenuList>
              <PopupMenuListItem>
                <PopupMenuButton
                  onClick={onAddGoogle}
                  start={<Icon icon={Google} />}
                >
                  {t(
                    'forms.user-settings.sections.calendar.add-external.google.label',
                  )}
                </PopupMenuButton>
              </PopupMenuListItem>
              <PopupMenuListItem>
                <PopupMenuButton
                  onClick={onAddOutlook}
                  start={<Icon icon={Outlook} />}
                >
                  {t(
                    'forms.user-settings.sections.calendar.add-external.outlook.label',
                  )}
                </PopupMenuButton>
              </PopupMenuListItem>
            </PopupMenuList>
          </PopupMenu>
        )}
      </Styled.ListItem>

      <Styled.ListItem>
        <CalendarSyncOptionsList />
      </Styled.ListItem>
    </Styled.List>
  );
};
